/** Quantity scale — mirrors backend org.qty_scale (default 1000 = 3 decimals). */

const DEFAULT_QTY_SCALE = 1000;

export function qtyScale(): number {
  const raw = import.meta.env.VITE_QTY_SCALE;
  const n = raw ? Number(raw) : DEFAULT_QTY_SCALE;
  return Number.isInteger(n) && n > 0 ? n : DEFAULT_QTY_SCALE;
}

function scaleDigits(scale: number): number {
  let digits = 0;
  let n = scale;
  while (n % 10 === 0 && n >= 10) {
    digits += 1;
    n /= 10;
  }
  return digits;
}

export function formatQtyScaled(qtyScaled: number, scale = qtyScale()): string {
  if (!Number.isInteger(qtyScaled)) return "0";
  const whole = Math.floor(qtyScaled / scale);
  const frac = qtyScaled % scale;
  const digits = scaleDigits(scale);
  if (frac === 0) return String(whole);
  const fracStr = String(frac).padStart(digits, "0").replace(/0+$/, "");
  return `${whole}.${fracStr}`;
}

/** Parse display quantity → integer qtyScaled for API payloads. */
export function parseToQtyScaled(
  qty: string | number,
  scale = qtyScale(),
): number {
  const raw = String(qty).trim().replace(/,/g, "");
  if (!raw) throw new Error("quantity is empty");

  if (!/^\d+(\.\d+)?$/.test(raw)) {
    throw new Error(`invalid quantity: ${qty}`);
  }

  const [wholePart, fracPart = ""] = raw.split(".");
  const digits = scaleDigits(scale);
  const fracPadded = (fracPart + "0".repeat(digits)).slice(0, digits);
  if (fracPart.length > digits && fracPart.slice(digits).replace(/0/g, "") !== "") {
    throw new Error(`too many decimal places for qty scale ${scale}`);
  }

  const result = Number(wholePart) * scale + Number(fracPadded || "0");
  if (!Number.isSafeInteger(result) || result <= 0) {
    throw new Error("quantity must be a positive integer at scale");
  }
  return result;
}
